import api from '../api/client';
import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Select, Typography, Row, Col } from 'antd';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import config from '../config';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement, Title, Tooltip, Legend);

const { Title: AntTitle } = Typography;
const { Option } = Select;

const AnimalMetrics = ({ animalType, animalCount }) => {
    const [metrics, setMetrics] = useState({});
    const [historicalData, setHistoricalData] = useState([]);
    const [timeRange, setTimeRange] = useState('7days');
    const environment = window.location.hostname === 'localhost' ? 'development' : 'production';
    const apiUrl = config[environment].apiUrl;
useEffect(() => {
        // fetch(`/api/animal-metrics?animalType=${animalType}`)
        //     .then((res) => res.json())
        //     .then((data) => setMetrics(data));

        fetch(`/api/historical-data?type=${animalType}&days=7`)
            .then((res) => res.json())
            .then((data) => setHistoricalData(data));
    }, [animalType]);

    // Production trend chart
    const productionChartData = {
        labels: historicalData.map((item) => {
            const date = new Date(item.date);
            return `${date.getMonth() + 1}/${date.getDate()}`;
        }),
        datasets: [
            {
                label: 'Health Index (%)',
                data: historicalData.map((item) => item.animalHealth),
                borderColor: 'rgb(75, 192, 192)',
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                fill: true,
                tension: 0.4
            },
            {
                label: animalType === 'poultry' ? 'Egg Production' : 'Production Index',
                data: historicalData.map((item) =>
                    animalType === 'poultry'
                        ? item.eggProduction / 3
                        : animalType === 'cattle'
                        ? item.milkProduction * 3
                        : item.weightGain * 40
                ),
                borderColor: 'rgb(153, 102, 255)',
                backgroundColor: 'rgba(153, 102, 255, 0.2)',
                fill: true,
                tension: 0.4
            }
        ]
    };

    const productionChartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top'
            },
            title: {
                display: true,
                text: `${animalType?.toUpperCase()} Health & Production Trend`
            }
        },
        scales: {
            y: {
                beginAtZero: false,
                title: {
                    display: true,
                    text: 'Index'
                }
            }
        }
    };

    // Metrics comparison chart
    const metricsChartData = {
        labels: Object.keys(metrics).map((key) => key.replace(/([A-Z])/g, ' $1')?.toUpperCase()),
        datasets: [
            {
                label: 'Current Values',
                data: Object.values(metrics).map((value) => {
                    // Extract numeric values from strings like "25 liters/day"
                    const num = parseFloat(value) || 0;
                    return isNaN(num) ? 0 : num;
                }),
                backgroundColor: [
                    'rgba(255, 99, 132, 0.8)',
                    'rgba(54, 162, 235, 0.8)',
                    'rgba(255, 206, 86, 0.8)',
                    'rgba(75, 192, 192, 0.8)',
                    'rgba(153, 102, 255, 0.8)'
                ],
                borderColor: [
                    'rgba(255, 99, 132, 1)',
                    'rgba(54, 162, 235, 1)',
                    'rgba(255, 206, 86, 1)',
                    'rgba(75, 192, 192, 1)',
                    'rgba(153, 102, 255, 1)'
                ],
                borderWidth: 1
            }
        ]
    };

    const metricsChartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top'
            },
            title: {
                display: true,
                text: 'Performance Metrics Comparison'
            }
        },
        scales: {
            y: {
                beginAtZero: true
            }
        }
    };

    // Health status doughnut chart
    const healthData = {
        labels: ['Excellent', 'Good', 'Fair', 'Poor'],
        datasets: [
            {
                data: [35, 40, 20, 5],
                backgroundColor: [
                    'rgba(75, 192, 192, 0.8)',
                    'rgba(54, 162, 235, 0.8)',
                    'rgba(255, 206, 86, 0.8)',
                    'rgba(255, 99, 132, 0.8)'
                ],
                borderColor: ['rgba(75, 192, 192, 1)', 'rgba(54, 162, 235, 1)', 'rgba(255, 206, 86, 1)', 'rgba(255, 99, 132, 1)'],
                borderWidth: 2
            }
        ]
    };

    const healthOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top'
            },
            title: {
                display: true,
                text: 'Animal Health Distribution'
            }
        }
    };

    const metricsData = Object.entries(metrics).map(([key, value]) => ({
        key,
        metric: key.replace(/([A-Z])/g, ' $1')?.toUpperCase(),
        value,
        status: key.includes('Rate') && parseFloat(value) > 5 ? 'high' : key.includes('Success') && parseFloat(value) < 80 ? 'low' : 'good'
    }));

    const columns = [
        {
            title: 'Metric',
            dataIndex: 'metric',
            key: 'metric'
        },
        {
            title: 'Value',
            dataIndex: 'value',
            key: 'value'
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => <Tag color={status === 'high' ? 'red' : status === 'low' ? 'orange' : 'green'}>{status.toUpperCase()}</Tag>
        }
    ];

    return (
        <Card
            title={`${animalType?.toUpperCase()} Performance Analytics`}
            extra={
                <Select value={timeRange} onChange={setTimeRange} style={{ width: 120 }}>
                    <Option value="7days">7 Days</Option>
                    <Option value="30days">30 Days</Option>
                    <Option value="90days">90 Days</Option>
                </Select>
            }
            bordered={false}
        >
            <Row gutter={[24, 24]}>
                <Col xs={24} lg={12}>
                    <Line data={productionChartData} options={productionChartOptions} />
                </Col>
                <Col xs={24} lg={12}>
                    <Bar data={metricsChartData} options={metricsChartOptions} />
                </Col>
                <Col xs={24} lg={8}>
                    <Doughnut data={healthData} options={healthOptions} />
                </Col>
                <Col xs={24} lg={16}>
                    <Table
                        dataSource={metricsData}
                        columns={columns}
                        pagination={false}
                        size="small"
                        title={() => <AntTitle level={5}>Current Performance Metrics</AntTitle>}
                    />
                </Col>
            </Row>
        </Card>
    );
};

export default AnimalMetrics;
